import React, { useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import Container from "../../components/Container";
import Main from "../../layouts/Main";
import { useAppSelector } from "../../hooks/hooks";
import { Compound } from "../../services/restserver-openapi";
import CompoundTable from "../../components/agentComponents/CompoundTable";

import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import RateReviewRoundedIcon from "@mui/icons-material/RateReviewRounded";

import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import RemoveRedEyeRoundedIcon from "@mui/icons-material/RemoveRedEyeRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";

export type CompoundFilters = {
    hostId: number;
    regionId: number;
    revised?: boolean;
    visible?: boolean;
    types: string[];
};

const AgentCompoundOverviewPage = () => {
    const theme = useTheme();
    const mode = theme.palette.mode;

    const isVerifiedAgent = useAppSelector((state) => state.user.isVerifiedAgent);

    // Pending (Sidebar UI)
    const [pendingHostId, setPendingHostId] = useState<number>(9999);
    const [pendingRevised, setPendingRevised] = useState<boolean | undefined>(undefined);
    const [pendingVisible, setPendingVisible] = useState<boolean | undefined>(undefined);
    const [pendingTypes, setPendingTypes] = useState<string[]>([]);

    // Applied (fetch-trigger für Table)
    const [appliedFilters, setAppliedFilters] = useState<CompoundFilters>({
        hostId: 9999,
        regionId: 0,
        revised: undefined,
        visible: undefined,
        types: [],
    });

    const compoundTypes = useMemo(() => {
        return Object.keys(Compound.compoundType).filter((k) => Number.isNaN(Number(k)));
    }, []);

    const applyFilters = () => {
        setAppliedFilters({
            hostId: pendingHostId,
            regionId: 0,
            revised: pendingRevised,
            visible: pendingVisible,
            types: pendingTypes,
        });
    };

    const resetFilters = () => {
        setPendingHostId(9999);
        setPendingRevised(undefined);
        setPendingVisible(undefined);
        setPendingTypes([]);

        setAppliedFilters({
            hostId: 21,
            regionId: 0,
            revised: undefined,
            visible: undefined,
            types: [],
        });
    };

    const ActionRow = (props: {
        icon: React.ReactNode;
        title: string;
        subtitle?: string;
        active?: boolean;
        onClick?: () => void;
    }) => {
        const { icon, title, subtitle, active, onClick } = props;

        return (
            <ListItem disablePadding>
                <ListItemButton
                    onClick={onClick}
                    selected={active}
                    sx={{
                        borderRadius: 2,
                        px: 1.25,
                        py: 1,
                        gap: 0.5,
                        "&.Mui-selected": {
                            bgcolor: "action.selected",
                            "&:hover": { bgcolor: "action.selected" },
                        },
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 40, color: active ? "primary.main" : "text.secondary" }}>
                        {icon}
                    </ListItemIcon>

                    <ListItemText
                        primary={
                            <Typography sx={{ fontWeight: 800 }} noWrap>
                                {title}
                            </Typography>
                        }
                        secondary={subtitle}
                        secondaryTypographyProps={{ color: "text.secondary" }}
                    />
                </ListItemButton>
            </ListItem>
        );
    };

    if (!isVerifiedAgent) {
        return (
            <Main>
                <Container>
                    <Paper elevation={0} sx={{ borderRadius: 4, p: 2, border: 0, boxShadow: "none", bgcolor: "background.paper" }}>
                        <Alert severity="error">
                            Sie haben keine Agent-Berechtigung. Womöglich ist Ihr Account noch nicht freigeschaltet.
                        </Alert>
                    </Paper>
                </Container>
            </Main>
        );
    }

    return (
        <Main>
            <Container>
                {/* Header im BookingOverviewPage-Style */}
                <Typography sx={{ textTransform: "uppercase", fontWeight: 500, letterSpacing: 1 }} gutterBottom color="text.secondary">
                    Administration
                </Typography>

                <Box mb={2}>
                    <Typography variant="h3" component="h2" color="text.primary" fontWeight={700} lineHeight={1.2}>
                        Compound{" "}
                        <Typography
                            component="span"
                            variant="inherit"
                            color="text.primary"
                            sx={{
                                background: `linear-gradient(180deg, transparent 82%, ${alpha(
                                    mode === "light" ? theme.palette.secondary.main : theme.palette.primary.main,
                                    0.3
                                )} 0%)`,
                            }}
                        >
                            Übersicht
                        </Typography>
                    </Typography>

                    <Box mb={3}>
                        <Typography variant="h6" color="text.secondary">
                            Nutze links die Filter, um die Compounds gezielt zu laden.
                        </Typography>
                    </Box>
                </Box>

                {/* Layout wie BookingOverview: Sidebar + Content */}
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "320px 1fr" }, gap: 2 }}>
                    {/* LEFT: Sidebar Filter */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            p: 2,
                            height: "fit-content",
                            border: 0,
                            boxShadow: "none",
                            bgcolor: "background.paper",
                        }}
                    >
                        <Typography variant="caption" fontWeight={900} sx={{ textTransform: "uppercase", letterSpacing: 1 }} color="text.secondary">
                            Filter
                        </Typography>

                        <Divider sx={{ my: 1.5 }} />

                        {/* Type + HostId */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Compound-Typ</InputLabel>
                                <Select
                                    multiple
                                    value={pendingTypes}
                                    onChange={(e) => setPendingTypes(e.target.value as string[])}
                                    label="Compound-Typ"
                                    renderValue={(selected) => (selected.length ? selected.join(", ") : "Alle")}
                                >
                                    {compoundTypes.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                size="small"
                                label="Host ID"
                                type="number"
                                value={pendingHostId}
                                onChange={(e) => setPendingHostId(Number(e.target.value))}
                                fullWidth
                            />
                        </Box>

                        {/* Revised ActionRows */}
                        <Typography variant="caption" fontWeight={900} sx={{ textTransform: "uppercase", letterSpacing: 1 }} color="text.secondary">
                            Revised
                        </Typography>

                        <List sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: 1, mb: 2 }}>
                            <ActionRow
                                icon={<ViewListRoundedIcon />}
                                title="Egal"
                                subtitle="Alle anzeigen"
                                active={pendingRevised === undefined}
                                onClick={() => setPendingRevised(undefined)}
                            />
                            <ActionRow
                                icon={<FactCheckRoundedIcon />}
                                title="Ja"
                                subtitle="Nur überarbeitete"
                                active={pendingRevised === true}
                                onClick={() => setPendingRevised(true)}
                            />
                            <ActionRow
                                icon={<RateReviewRoundedIcon />}
                                title="Nein"
                                subtitle="Nur nicht überarbeitete"
                                active={pendingRevised === false}
                                onClick={() => setPendingRevised(false)}
                            />
                        </List>

                        {/* Visible ActionRows */}
                        <Typography variant="caption" fontWeight={900} sx={{ textTransform: "uppercase", letterSpacing: 1 }} color="text.secondary">
                            Visible
                        </Typography>

                        <List sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: 1, mb: 2 }}>
                            <ActionRow
                                icon={<VisibilityOutlinedIcon />}
                                title="Egal"
                                subtitle="Visible & Hidden"
                                active={pendingVisible === undefined}
                                onClick={() => setPendingVisible(undefined)}
                            />
                            <ActionRow
                                icon={<RemoveRedEyeRoundedIcon />}
                                title="Ja"
                                subtitle="Nur sichtbare"
                                active={pendingVisible === true}
                                onClick={() => setPendingVisible(true)}
                            />
                            <ActionRow
                                icon={<VisibilityOffRoundedIcon />}
                                title="Nein"
                                subtitle="Nur versteckte (Incognito)"
                                active={pendingVisible === false}
                                onClick={() => setPendingVisible(false)}
                            />
                        </List>

                        <Divider sx={{ my: 1.25 }} />

                        <Button variant="contained" fullWidth onClick={applyFilters}>
                            Aktualisieren
                        </Button>

                        <Button variant="text" fullWidth onClick={resetFilters} sx={{ fontWeight: 800, mt: 0.5 }}>
                            Zurücksetzen
                        </Button>
                    </Paper>

                    {/* RIGHT: Content */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            p: 2,
                            border: 0,
                            boxShadow: "none",
                            bgcolor: "background.paper",
                        }}
                    >
                        <CompoundTable filters={appliedFilters} />
                    </Paper>
                </Box>
            </Container>
        </Main>
    );
};

export default AgentCompoundOverviewPage;
